import { useState, useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@hybrid-iptv/ui";
import { forgetEpgCache } from "../epg/epgCache.js";
import {
  MIN_PROVIDER_CONTENT,
  cleanConnection,
  makeProvider,
  providerHasContent,
  providerId,
  useSettings,
  type ProviderConfig,
  type ProviderKind,
} from "../settings/settingsStore.js";
import { api, ApiError, runsOnDevice } from "../api/client.js";
import { brand } from "../brand.js";
import { FREE_LIMITS, requireFeature } from "../library/proFeatures.js";
import { GuideSources } from "../settings/GuideSources.js";

/**
 * Credentials are typed on phones and TV remotes, where the keyboard "helps":
 * it capitalises the first letter of a text field, autocorrects a random
 * username into a word, and appends a space after predictive input. Any one of
 * those is a wrong login — and the panel that exposed this drops the connection
 * on a wrong login, so the viewer saw "Server unreachable" for a typing slip
 * (owner's second provider, 2026-09-17). Nothing here is prose to be corrected.
 */
const CREDENTIAL_INPUT = {
  autoCapitalize: "none",
  autoCorrect: "off",
  spellCheck: false,
} as const;

/**
 * Multi-provider setup — a Tivimate-style list of providers that coexist on
 * this device. One provider is active at a time; the active provider is what
 * Home / Live / Movies / Series / Search query (per-provider device caches mean
 * switching is instant and nothing is overwritten). Each provider keeps its own
 * name and credentials, is validated independently, and can be edited/removed.
 */
/** Where the settings live, in the words of the device the viewer holds (a TV is not "this browser"). */
const savedNote = () => `Settings are saved automatically on this ${runsOnDevice() ? "device" : "browser"}.`;

export function SourcePage() {
  const {
    providers,
    activeProviderId,
    activeProvider,
    addProvider,
    updateProvider,
    removeProvider,
    setActiveProvider,
  } = useSettings();
  const queryClient = useQueryClient();

  // Editing state: null = list view; ProviderConfig = editing a draft.
  const [draft, setDraft] = useState<ProviderConfig | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  // Two-step confirm rather than a modal: removal is destructive, but a dialog
  // adds a focus trap to escape on a D-pad. The button becomes its own
  // confirmation, so remote, mouse and touch all take the same path.
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  // A tenant's app (brand().portals): viewers pick one of its portals and only type their
  // own username and password. There is no address box and no M3U (ADR 2026-10-08).
  const portals = brand().portals;
  const portalOnly = portals.length > 0;
  const startNew = () => {
    // A free viewer has one playlist (ADR 2026-10-10); a second one asks. Tenants are never held to it.
    if (providers.length >= FREE_LIMITS.playlists && !requireFeature("playlists")) return;
    startNewDraft();
  };
  const startNewDraft = () =>
    setDraft(
      portalOnly
        ? makeProvider({ playlistName: portals[0]!.label, xtreamBaseUrl: portals[0]!.url })
        : makeProvider({ playlistName: `Source ${providers.length + 1}` }),
    );
  const pickPortal = (url: string) => {
    const portal = portals.find((p) => p.url === url);
    if (!portal) return;
    setDraft((d) => {
      if (!d) return d;
      // The name follows the portal unless the viewer typed one of their own.
      const named = !portals.some((p) => p.label === d.playlistName);
      return { ...d, xtreamBaseUrl: portal.url, playlistName: named ? d.playlistName : portal.label };
    });
  };
  const startEdit = (p: ProviderConfig) => {
    setDraft({ ...p });
    setTestResult(null);
  };
  const cancelEdit = () => {
    setDraft(null);
    setTestResult(null);
  };

  const setKind = (kind: ProviderKind) => {
    if (!draft) return;
    setDraft((d) => (d ? { ...d, kind } : d));
  };

  const handleRemove = (id: string) => {
    if (confirmRemoveId !== id) {
      setConfirmRemoveId(id);
      return;
    }
    removeProvider(id);
    setConfirmRemoveId(null);
    setTestResult(null);
  };

  const handleSave = () => {
    if (!draft) return;
    // The id derives from the CURRENT connection details — a draft created
    // empty then filled in would otherwise keep the empty-draft id and collide
    // with (and overwrite) a different provider.
    // addProvider cleans the connection itself; the id is computed from the
    // same trimmed values, so it matches what gets stored.
    const id = providerId(cleanConnection(draft));
    const before = providers.find((p) => p.id === id);
    addProvider({ ...draft, id });
    // A new guide feed must replace the old one's cached guide, not sit behind it.
    if (before && (before.epgUrl ?? "").trim() !== (draft.epgUrl ?? "").trim()) {
      void forgetEpgCache(id).then(() => {
        queryClient.removeQueries({ queryKey: ["epg-cache", id] });
        queryClient.removeQueries({ queryKey: ["epg", id] });
      });
    }
    setDraft(null);
    setTestResult(null);
  };

  const handleTest = useCallback(async () => {
    if (!draft) return;
    setTesting(true);
    setTestResult(null);
    try {
      // Test exactly what will be saved.
      const conn = cleanConnection(draft);
      const body =
        conn.kind === "xtream"
          ? { kind: "xtream" as const, xtreamBaseUrl: conn.xtreamBaseUrl, xtreamUsername: conn.xtreamUsername, xtreamPassword: conn.xtreamPassword }
          : { kind: "m3u" as const, m3uUrl: conn.m3uUrl };
      const result = await api.post<{
        ok: boolean;
        message: string;
        channelCount?: number;
        movieCount?: number;
        seriesCount?: number;
        contentCount?: number;
      }>("/api/sources/validate", body);
      const contentCount = result.contentCount ?? result.channelCount ?? 0;
      // Connecting is not enough — a panel can authenticate and still serve an
      // empty catalog, which would put the user into an app of blank rails.
      const enough = contentCount >= MIN_PROVIDER_CONTENT;
      const ok = result.ok && enough;
      const message = !result.ok
        ? result.message
        : enough
          ? `Connected — ${contentCount.toLocaleString()} items (${(result.channelCount ?? 0).toLocaleString()} channels, ${(result.movieCount ?? 0).toLocaleString()} movies, ${(result.seriesCount ?? 0).toLocaleString()} series).`
          : `Connected, but this source only returned ${contentCount} item${contentCount === 1 ? "" : "s"}. At least ${MIN_PROVIDER_CONTENT} are needed before you can continue.`;
      // Persist validation result back onto the provider connection.
      const patch = {
        lastValidated: Date.now(),
        isValid: ok,
        lastError: ok ? null : message,
        contentCount,
      };
      updateProvider(draft.id, patch);
      setDraft((d) => (d ? { ...d, ...patch } : d));
      setTestResult({ ok, message });
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "Network error — is the server reachable?";
      updateProvider(draft.id, { lastValidated: Date.now(), isValid: false, lastError: message });
      setDraft((d) => (d ? { ...d, lastValidated: Date.now(), isValid: false, lastError: message } : d));
      setTestResult({ ok: false, message });
    } finally {
      setTesting(false);
    }
  }, [draft, updateProvider]);

  // Something always holds the ring, so the first OK acts: the first-run list had no
  // focus at all, and opening a source dropped focus when Add Source went away
  // (owner, 2026-10-08). Only when nothing on the page is focused already.
  const pageRef = useRef<HTMLDivElement>(null);
  const editing = draft !== null;
  useEffect(() => {
    const page = pageRef.current;
    const active = document.activeElement;
    if (!page || (active instanceof HTMLElement && active !== document.body && page.contains(active))) return;
    const target = editing
      ? page.querySelector<HTMLElement>("input, select, button")
      : page.querySelector<HTMLElement>(".provider-item--active, .provider-item, .settings-actions button");
    target?.focus();
  }, [editing]);

  // ---- List view (Tivimate-style) ----
  if (!draft) {
    return (
      <div className="page page--source" ref={pageRef}>
        <div className="settings-card">
          <div className="settings-header">
            <img className="settings-logo" src={brand().icon} alt={brand().appName} />
            <h1 className="settings-title">Sources</h1>
            <p className="settings-subtitle">
              Add your streaming sources (primary, backups, or multiple servers).
              Pick one to make it active.
            </p>
          </div>

          <div className="provider-list">
            {providers.length === 0 && (
              <p className="settings-save-note">No sources yet — add your first one below.</p>
            )}
            {providers.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`provider-item ${p.id === activeProviderId ? "provider-item--active" : ""}`}
                onClick={() => { setActiveProvider(p.id); setConfirmRemoveId(null); }}
              >
                <span className="provider-item__name">{p.playlistName || p.xtreamBaseUrl || p.m3uUrl || "Unnamed source"}</span>
                <span className={`provider-item__status ${p.isValid ? "provider-item__status--ok" : ""}`}>
                  {p.id === activeProviderId ? "● Active" : ""} {p.isValid ? "✓" : "•"}
                </span>
              </button>
            ))}
          </div>

          {/* Saving a provider is not the same as being able to use it: the
              app gates on validated content (MIN_PROVIDER_CONTENT). Without
              this note a user who saved an empty or typo'd panel would sit on
              the first-run screen with no idea what to do next. */}
          {activeProvider && !providerHasContent(activeProvider) && (
            <p className="settings-save-note">
              {activeProvider.isValid === false && activeProvider.lastError
                ? activeProvider.lastError
                : `Run Test Connection on "${activeProvider.playlistName || "this source"}" — it needs to return at least ${MIN_PROVIDER_CONTENT} channels, movies or series before you can continue.`}
            </p>
          )}

          <div className="settings-actions">
            <Button variant="primary" onClick={startNew}>
              + Add Source
            </Button>
            {activeProvider && providers.some((p) => p.id === activeProvider.id) && (
              <>
                <Button variant="default" onClick={() => startEdit(activeProvider)}>
                  Edit Active
                </Button>
                <Button
                  variant={confirmRemoveId === activeProvider.id ? "danger" : "ghost"}
                  onClick={() => handleRemove(activeProvider.id)}
                >
                  {confirmRemoveId === activeProvider.id ? "Confirm Remove" : "Remove Active"}
                </Button>
              </>
            )}
          </div>
          <p className="settings-save-note">{savedNote()}</p>
        </div>
      </div>
    );
  }

  // ---- Edit view ----
  return (
    <div className="page page--source" ref={pageRef}>
      <div className="settings-card">
        <div className="settings-header">
          <img className="settings-logo" src={brand().icon} alt={brand().appName} />
          <h1 className="settings-title">{providers.some((p) => p.id === draft.id) ? `Edit: ${draft.playlistName || "source"}` : "New Source"}</h1>
          <p className="settings-subtitle">Enter your source connection details.</p>
        </div>

        <label className="settings-field">
          <span className="settings-label">Playlist Name</span>
          <input type="text" className="settings-input" placeholder="e.g. My Source" value={draft.playlistName} onChange={(e) => setDraft((d) => (d ? { ...d, playlistName: e.target.value } : d))} />
        </label>

        {!portalOnly && (
          <div className="settings-field">
            <span className="settings-label">Source Type</span>
            <div className="settings-toggle">
              <button type="button" className={`settings-toggle-btn ${draft.kind === "xtream" ? "settings-toggle-btn--active" : ""}`} onClick={() => setKind("xtream")}>Xtream Codes</button>
              <button type="button" className={`settings-toggle-btn ${draft.kind === "m3u" ? "settings-toggle-btn--active" : ""}`} onClick={() => setKind("m3u")}>M3U URL</button>
            </div>
          </div>
        )}

        {(draft.kind === "xtream" || portalOnly) && (
          <div className="settings-section">
            {portalOnly ? (
              portals.length > 1 ? (
                <label className="settings-field">
                  <span className="settings-label">Server</span>
                  <select className="settings-input" value={draft.xtreamBaseUrl} onChange={(e) => pickPortal(e.target.value)}>
                    {portals.map((p) => (
                      <option key={p.url} value={p.url}>{p.label}</option>
                    ))}
                  </select>
                </label>
              ) : null
            ) : (
              <label className="settings-field">
                <span className="settings-label">Server URL</span>
                <input type="url" className="settings-input" placeholder="http://your-panel.cc:8080" {...CREDENTIAL_INPUT} value={draft.xtreamBaseUrl} onChange={(e) => setDraft((d) => (d ? { ...d, xtreamBaseUrl: e.target.value } : d))} />
              </label>
            )}
            <label className="settings-field">
              <span className="settings-label">Username</span>
              <input type="text" className="settings-input" placeholder="Your Xtream username" {...CREDENTIAL_INPUT} value={draft.xtreamUsername} onChange={(e) => setDraft((d) => (d ? { ...d, xtreamUsername: e.target.value } : d))} />
            </label>
            <label className="settings-field">
              <span className="settings-label">Password</span>
              <input type="password" className="settings-input" placeholder="Your Xtream password" {...CREDENTIAL_INPUT} autoComplete="off" value={draft.xtreamPassword} onChange={(e) => setDraft((d) => (d ? { ...d, xtreamPassword: e.target.value } : d))} />
            </label>
          </div>
        )}

        {draft.kind === "m3u" && !portalOnly && (
          <div className="settings-section">
            <label className="settings-field">
              <span className="settings-label">M3U Playlist URL</span>
              <input type="url" className="settings-input" placeholder="https://example.com/playlist.m3u" {...CREDENTIAL_INPUT} value={draft.m3uUrl} onChange={(e) => setDraft((d) => (d ? { ...d, m3uUrl: e.target.value } : d))} />
            </label>
            <label className="settings-field">
              <span className="settings-label">EPG URL (optional)</span>
              <input type="url" className="settings-input" placeholder="https://example.com/guide.xml" {...CREDENTIAL_INPUT} value={draft.epgUrl ?? ""} onChange={(e) => setDraft((d) => (d ? { ...d, epgUrl: e.target.value } : d))} />
              <span className="settings-hint">Leave empty to use the guide the playlist names, if it names one.</span>
            </label>
          </div>
        )}

        {testResult && (
          <div className={`settings-result ${testResult.ok ? "settings-result--ok" : "settings-result--fail"}`}>
            {testResult.ok ? "✓" : "✗"} {testResult.message}
          </div>
        )}
        {draft.lastValidated && (
          <div className="settings-meta">
            Last tested: {new Date(draft.lastValidated).toLocaleString()}
            {draft.isValid ? " — ✓ valid" : " — ✗ failed"}
          </div>
        )}

        <div className="settings-actions">
          <Button variant="primary" disabled={testing} onClick={handleTest}>
            {testing ? "Testing…" : "Test Connection"}
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save Source
          </Button>
          <Button variant="default" onClick={cancelEdit}>
            Back
          </Button>
        </div>
        <p className="settings-save-note">{savedNote()}</p>
        {/* A saved playlist's guide sources (Phase 3 step 3): below everything else, so the
            fields and buttons above keep their places. */}
        {providers.some((p) => p.id === draft.id) && <GuideSources providerId={draft.id} />}
      </div>
    </div>
  );
}